/*
 * BNO PDP loading + scroll-entry animations.
 *
 * Loaded once (from the explore section, which always renders) as a deferred
 * module, so by the time it runs the whole PDP DOM is parsed and it can see the
 * hooks in every section.
 *
 * Everything here is a NO-OP unless html.bno-js is set (added at first paint by
 * the hero) AND the visitor has not requested reduced motion. The hiding CSS in
 * bno-shared.css is gated the same way, so with JS disabled or reduced motion on
 * nothing is ever hidden: content is simply visible and static.
 *
 * Jobs in this file:
 *   - Fade each below-the-fold image (the .bno-img hook) in once it has actually
 *     decoded, so images never pop in half-drawn.
 *   - Reveal section content (the .bno-reveal hook) with a fade + slide-up the
 *     first time it scrolls into view, once only.
 */
(() => {
  const root = document.documentElement;
  // Boot class is normally set inline by the hero for a flash-free first paint;
  // set it here too so the hooks still work if that inline boot is ever absent
  // (in that case there may be a brief flash, but content is never stuck hidden).
  root.classList.add('bno-js');

  if (window.__bnoScrollInit) return;
  window.__bnoScrollInit = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- Image fade-on-load ----------------------------------------------------
  // The image starts at opacity 0 (only under html.bno-js + no-preference, see
  // bno-shared.css). We add .is-loaded once the bitmap is ready to paint, using
  // the load event (which respects loading="lazy": we never force an early
  // fetch) and then decode() to be sure it is paint-ready, not half-drawn.
  const show = (img) => img.classList.add('is-loaded');
  const onReady = (img) => {
    if (typeof img.decode === 'function') {
      img.decode().then(() => show(img)).catch(() => show(img));
    } else {
      show(img);
    }
  };

  document.querySelectorAll('img.bno-img').forEach((img) => {
    if (reduce) {
      show(img);
      return;
    }
    if (img.complete && img.naturalWidth > 0) {
      onReady(img);
      return;
    }
    img.addEventListener('load', () => onReady(img), { once: true });
    // On error, still reveal it rather than leaving a permanently blank box.
    img.addEventListener('error', () => show(img), { once: true });
  });

  // --- Section entry reveal --------------------------------------------------
  // .bno-reveal          : the element itself fades + slides up.
  // .bno-reveal-group    : a container that only receives .is-in on entry; its
  //                        CSS staggers its CHILDREN in (used by the showcase
  //                        carousel, whose cards must all reveal from one
  //                        always-full-width trigger, not per-card — off-screen
  //                        cards would otherwise never trigger and later cards
  //                        would inherit huge delays).
  const revealEls = document.querySelectorAll('.bno-reveal, .bno-reveal-group');
  if (!revealEls.length) return;

  // No motion, or no observer support: show everything immediately (the CSS
  // hiding rule is already suppressed under reduced motion, so this is belt and
  // braces for the no-IntersectionObserver case).
  if (reduce || !('IntersectionObserver' in window)) {
    revealEls.forEach((el) => el.classList.add('is-in'));
    return;
  }

  // Reveal trigger: an element fires once it has risen a fraction of the
  // viewport height above the bottom edge (a negative bottom rootMargin), so it
  // is well onto the screen rather than the instant its first pixel appears.
  // Split by breakpoint — a shallower 5% on mobile (<=749px) so content on short
  // screens reveals sooner, and 10% on desktop (>749px). Evaluated once at load,
  // matching this module's one-shot design (it does not re-arm on resize).
  const desktop = window.matchMedia('(min-width: 750px)').matches;
  const revealMargin = desktop ? '0px 0px -10% 0px' : '0px 0px -5% 0px';

  const io = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target); // play once; no replay on scroll-up
      }
    },
    { rootMargin: revealMargin, threshold: 0 }
  );

  revealEls.forEach((el) => io.observe(el));
})();
